import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { useTenant } from '../contexts/TenantContext';
import { API_BASE_URL, buildApiUrl } from '../services/api';
import { useRuntimeCapabilities } from '../hooks/useRuntimeCapabilities';

type SessionStatus = 'WORKING' | 'SCAN_QR_CODE' | 'STOPPED' | 'FAILED';
type SessionProvider = 'WAHA' | 'EVOLUTION';
type InboxMediaType = 'image' | 'audio' | 'document';

interface WhatsAppSession {
  name: string;
  displayName?: string;
  status: SessionStatus;
  provider: SessionProvider;
  me?: {
    id: string;
    pushName: string;
  };
}

interface InboxMessage {
  id: string;
  chatId: string;
  from: string | null;
  to: string | null;
  fromMe: boolean;
  body: string;
  timestamp: number;
  type: string;
  hasMedia: boolean;
  mediaUrl: string | null;
  mimeType: string | null;
  fileName: string | null;
  ack: number | null;
  ackName: string | null;
  quotedMessageId: string | null;
  optimistic?: boolean;
}

interface InboxChatSummary {
  id: string;
  name: string;
  picture: string | null;
  unreadCount: number;
  archived: boolean;
  isGroup: boolean;
  phone: string | null;
  lastActivityTimestamp: number | null;
  lastMessage: InboxMessage | null;
  participants?: Array<unknown> | null;
}

interface ChatCertificateContext {
  campaignId: string | null;
  campaignName: string | null;
  campaignMessageId: string | null;
  sessionName: string;
  chatId: string;
  normalizedPhone: string | null;
  identifiedRecipientName: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  contactDocument: string | null;
  contactAddress: string | null;
  contactProcess: string | null;
  contactMandate: string | null;
  contactCategory: string | null;
  contactNotes: string | null;
  mandateId: string | null;
  mandateRecipientName: string | null;
  mandateAddress: string | null;
  mandateStatus: string | null;
  mandateCaseNumber: string | null;
  mandateType: string | null;
  sentAt: string | null;
  createdAt: string | null;
  conversationStartedAt: string | null;
  conversationLastMessageAt: string | null;
  conversationMessageCount: number;
  conversationSummary: string | null;
  conversationTranscript: string | null;
}

type SocketLike = {
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off: (event: string, handler?: (...args: unknown[]) => void) => void;
  disconnect: () => void;
};

declare global {
  interface Window {
    io?: (url: string, options?: Record<string, unknown>) => SocketLike;
    __auxiliajudSocketIoLoader?: Promise<void>;
  }
}

const DEFAULT_QUICK_TEXTS: { category: string; items: { id: string; label: string; text: string }[] }[] = [
  {
    category: 'Cumprimentos',
    items: [
      { id: 'q-bom-dia', label: 'Bom dia', text: 'Bom dia! Em que posso ajudá-lo(a)?' },
      { id: 'q-boa-tarde', label: 'Boa tarde', text: 'Boa tarde! Como posso auxiliá-lo(a)?' },
      { id: 'q-ola', label: 'Olá', text: 'Olá! Precisa de algo? Estou à disposição.' },
    ],
  },
  {
    category: 'Diligências',
    items: [
      { id: 'q-dilig-ok', label: 'Diligência realizada', text: 'Informamos que a diligência referente ao seu processo foi realizada com sucesso.' },
      { id: 'q-dilig-ausente', label: 'Ausente no local', text: 'Realizamos uma tentativa de diligência, porém não localizamos o destinatário no endereço informado. Realizaremos nova tentativa em breve.' },
      { id: 'q-dilig-confirmar', label: 'Confirmar endereço', text: 'Para realizarmos a diligência, precisamos confirmar o endereço atualizado. Poderia nos informar?' },
      { id: 'q-dilig-horario', label: 'Informar horário', text: 'Poderia nos informar um horário de preferência para que possamos realizar a diligência?' },
    ],
  },
  {
    category: 'Documentos',
    items: [
      { id: 'q-doc-anexo', label: 'Docs em anexo', text: 'Segue em anexo a documentação referente ao seu processo. Qualquer dúvida, estamos à disposição.' },
      { id: 'q-doc-aguardando', label: 'Aguardando docs', text: 'Aguardamos o retorno com os documentos solicitados para continuidade do processo.' },
    ],
  },
  {
    category: 'Encerramentos',
    items: [
      { id: 'q-enc-disposicao', label: 'À disposição', text: 'Fico à disposição para qualquer dúvida ou informação adicional.' },
      { id: 'q-enc-contato', label: 'Contato futuro', text: 'Caso precise de mais informações, não hesite em nos contatar. Atenciosamente.' },
      { id: 'q-enc-encerrado', label: 'Atendimento encerrado', text: 'A demanda foi finalizada com sucesso. Obrigado pelo contato!' },
    ],
  },
];

const QUICK_TEXTS_STORAGE_KEY = 'whatsapp_quick_texts';

interface QuickTextModalState {
  mode: 'create' | 'edit';
  originalId?: string;
  originalCategory?: string;
  category: string;
  label: string;
  text: string;
}

const MOCK_CHAT_SESSION = 'mock-evolution-session';

const buildMockData = () => {
  const now = Math.floor(Date.now() / 1000);

  const makeMessage = (
    id: string,
    chatId: string,
    fromMe: boolean,
    body: string,
    timestamp: number,
    authorName: string
  ): InboxMessage => ({
    id,
    chatId,
    from: fromMe ? 'Sistema' : authorName,
    to: fromMe ? chatId : 'Mock Session',
    fromMe,
    body,
    timestamp,
    type: 'text',
    hasMedia: false,
    mediaUrl: null,
    mimeType: null,
    fileName: null,
    ack: null,
    ackName: fromMe ? 'Entregue' : null,
    quotedMessageId: null
  });

  const chatAId = '5511999990001@c.us';
  const chatBId = '5511888880002@c.us';

  const messagesA: InboxMessage[] = [
    makeMessage('mock-a-1', chatAId, false, 'Bom dia! Gostaria de saber o andamento do mandado.', now - 1800, 'Cliente 01'),
    makeMessage('mock-a-2', chatAId, true, 'Perfeito, vou verificar e te atualizo em instantes.', now - 1710, 'Cliente 01')
  ];

  const longConversationBodies = [
    'Oi, preciso confirmar o endereço da diligência para hoje.',
    'Claro. Pode confirmar o número do processo e o bairro?',
    'Processo 0001234-56.2026.8.26.0001, bairro Centro.',
    'Recebido. A intimação será feita no período da tarde.',
    'Perfeito, consegue me enviar um retorno quando concluir?',
    'Sim, assim que finalizar envio um resumo da diligência.',
    'O destinatário costuma estar no local após as 16h.',
    'Ótimo ponto, vou priorizar essa faixa de horário.',
    'Se não encontrar, tem tentativa alternativa amanhã cedo?',
    'Temos sim. Posso programar nova tentativa para 08h30.',
    'Obrigado! Também preciso anexar observação no relatório.',
    'Sem problema, incluirei observações detalhadas no retorno.',
    'Consegue registrar se houve recusa de recebimento?',
    'Sim, caso ocorra recusa isso constará expressamente.',
    'Excelente. Estou acompanhando por aqui.',
    'Atualizando: em deslocamento para o endereço informado.',
    'Tudo certo, fico no aguardo da conclusão.',
    'Cheguei ao local, aguardando o destinatário.',
    'Perfeito. Qualquer novidade me sinalize.',
    'Destinatário localizado. Procedendo com a intimação agora.',
    'Ótimo, essa informação já ajuda bastante.',
    'Intimação concluída com sucesso, sem intercorrências.',
    'Excelente trabalho. Pode compartilhar o resumo final?',
    'Resumo: diligência positiva, destinatário ciente e assinou.',
    'Perfeito, vou fechar esse atendimento com essa certidão.'
  ];

  const messagesB: InboxMessage[] = longConversationBodies.map((body, index) => {
    const fromMe = index % 2 === 1;
    const timestamp = now - (1600 - index * 50);
    return makeMessage(`mock-b-${index + 1}`, chatBId, fromMe, body, timestamp, 'Cliente 02');
  });

  const messagesByChatId: Record<string, InboxMessage[]> = {
    [chatAId]: messagesA,
    [chatBId]: messagesB
  };

  const lastMessageA = messagesA[messagesA.length - 1] || null;
  const lastMessageB = messagesB[messagesB.length - 1] || null;

  const chats: InboxChatSummary[] = [
    {
      id: chatBId,
      name: 'Cliente 02',
      picture: null,
      unreadCount: 1,
      archived: false,
      isGroup: false,
      phone: '5511888880002',
      lastActivityTimestamp: lastMessageB?.timestamp || null,
      lastMessage: lastMessageB
    },
    {
      id: chatAId,
      name: 'Cliente 01',
      picture: null,
      unreadCount: 0,
      archived: false,
      isGroup: false,
      phone: '5511999990001',
      lastActivityTimestamp: lastMessageA?.timestamp || null,
      lastMessage: lastMessageA
    }
  ];

  return { chats, messagesByChatId };
};

const formatListTime = (timestamp?: number | null) => {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp * 1000);
  const now = new Date();
  const isSameDay = date.toDateString() === now.toDateString();

  if (isSameDay) {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit'
  }).format(date);
};

const formatMessageTime = (timestamp: number) => {
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(timestamp * 1000));
};

const getChatRecencyTimestamp = (chat: InboxChatSummary) => {
  return chat.lastMessage?.timestamp || chat.lastActivityTimestamp || 0;
};

const compareChatsByRecency = (left: InboxChatSummary, right: InboxChatSummary) => {
  const leftTimestamp = getChatRecencyTimestamp(left);
  const rightTimestamp = getChatRecencyTimestamp(right);

  if (rightTimestamp !== leftTimestamp) {
    return rightTimestamp - leftTimestamp;
  }

  const leftHasMessages = Boolean(left.lastMessage);
  const rightHasMessages = Boolean(right.lastMessage);

  if (leftHasMessages !== rightHasMessages) {
    return rightHasMessages ? 1 : -1;
  }

  return left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' });
};

const getChatIdentity = (chat: InboxChatSummary | null) => {
  if (!chat) {
    return '';
  }

  return normalizeBrazilianPhone(chat.phone || chat.id || chat.name) || chat.id.trim().toLowerCase();
};

const getChatIdentityFromValue = (value?: string | null) => {
  const normalizedPhone = normalizeBrazilianPhone(value);
  if (normalizedPhone) {
    return normalizedPhone;
  }

  return String(value || '').trim().toLowerCase();
};

const getMessageCacheKey = (chatId?: string | null) => getChatIdentityFromValue(chatId);

const mergeMessageLists = (currentMessages: InboxMessage[], incomingMessages: InboxMessage[]) => {
  const merged = new Map<string, InboxMessage>();

  for (const message of currentMessages) {
    if (message.id) {
      merged.set(message.id, message);
    }
  }

  for (const message of incomingMessages) {
    if (!message.id) {
      continue;
    }

    const existing = merged.get(message.id);
    merged.set(message.id, existing ? { ...existing, ...message } : message);
  }

  return Array.from(merged.values()).sort((left, right) => left.timestamp - right.timestamp);
};

const mergeMessagesIntoCache = (
  currentMap: Record<string, InboxMessage[]>,
  chatId: string,
  incomingMessages: InboxMessage[]
) => {
  const cacheKey = getMessageCacheKey(chatId);
  return {
    ...currentMap,
    [cacheKey]: mergeMessageLists(currentMap[cacheKey] || [], incomingMessages),
  };
};

const replaceOptimisticMessage = (
  currentMessages: InboxMessage[],
  optimisticMessageId: string,
  persistedMessage: InboxMessage
) => {
  const withoutOptimistic = currentMessages.filter((message) => message.id !== optimisticMessageId);
  return mergeMessageLists(withoutOptimistic, [{ ...persistedMessage, optimistic: false }]);
};

const mergeChatSummaries = (baseChat: InboxChatSummary, nextChat: InboxChatSummary): InboxChatSummary => {
  const baseTimestamp = getChatRecencyTimestamp(baseChat);
  const nextTimestamp = getChatRecencyTimestamp(nextChat);
  const newestMessage = nextTimestamp >= baseTimestamp ? (nextChat.lastMessage || baseChat.lastMessage) : (baseChat.lastMessage || nextChat.lastMessage);

  return {
    ...baseChat,
    id: nextChat.id || baseChat.id,
    name: nextChat.name || baseChat.name,
    picture: nextChat.picture || baseChat.picture,
    unreadCount: Math.max(baseChat.unreadCount || 0, nextChat.unreadCount || 0),
    archived: nextChat.archived,
    isGroup: nextChat.isGroup || baseChat.isGroup,
    phone: nextChat.phone || baseChat.phone,
    lastActivityTimestamp: Math.max(baseChat.lastActivityTimestamp || 0, nextChat.lastActivityTimestamp || 0, newestMessage?.timestamp || 0) || null,
    lastMessage: newestMessage ? { ...newestMessage, chatId: nextChat.id || newestMessage.chatId } : null,
    participants: Array.isArray(nextChat.participants) && nextChat.participants.length > 0
      ? nextChat.participants
      : baseChat.participants,
  };
};

const consolidateChatSummaries = (chatList: InboxChatSummary[]) => {
  const merged = new Map<string, InboxChatSummary>();

  for (const chat of chatList) {
    if (!chat?.id) {
      continue;
    }

    const identity = getChatIdentity(chat);
    const existing = merged.get(identity);
    merged.set(identity, existing ? mergeChatSummaries(existing, chat) : chat);
  }

  return Array.from(merged.values()).sort(compareChatsByRecency);
};

const buildRealtimeChatFromMessage = (message: InboxMessage): InboxChatSummary => ({
  id: message.chatId,
  name: message.fromMe ? formatWhatsAppPhoneFallback(message.chatId) : (message.from || formatWhatsAppPhoneFallback(message.chatId)),
  picture: null,
  unreadCount: message.fromMe ? 0 : 1,
  archived: false,
  isGroup: message.chatId.includes('@g.us'),
  phone: normalizeBrazilianPhone(message.chatId),
  lastActivityTimestamp: message.timestamp,
  lastMessage: message,
  participants: null,
});

const applyMessagesToChatList = (
  currentChats: InboxChatSummary[],
  incomingMessages: InboxMessage[],
  activeChatIdentity: string
) => {
  const chatsByIdentity = new Map<string, InboxChatSummary>(
    consolidateChatSummaries(currentChats).map((chat) => [getChatIdentity(chat), chat])
  );

  for (const message of incomingMessages) {
    const chatIdentity = getChatIdentityFromValue(message.chatId);
    const existingChat = chatsByIdentity.get(chatIdentity);
    const baseChat = existingChat || buildRealtimeChatFromMessage(message);
    const unreadCount = message.fromMe || chatIdentity === activeChatIdentity
      ? 0
      : (existingChat?.unreadCount || 0) + 1;

    chatsByIdentity.set(chatIdentity, {
      ...baseChat,
      id: message.chatId || baseChat.id,
      phone: normalizeBrazilianPhone(message.chatId) || baseChat.phone,
      unreadCount,
      lastActivityTimestamp: message.timestamp,
      lastMessage: message,
    });
  }

  return Array.from(chatsByIdentity.values()).sort(compareChatsByRecency);
};

const resolveSocketOrigin = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return new URL(API_BASE_URL, window.location.origin).origin;
};

const loadSocketIoClient = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.io) {
    return;
  }

  if (!window.__auxiliajudSocketIoLoader) {
    window.__auxiliajudSocketIoLoader = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${resolveSocketOrigin()}/socket.io/socket.io.js`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Não foi possível carregar o cliente Socket.IO.'));
      document.head.appendChild(script);
    });
  }

  await window.__auxiliajudSocketIoLoader;
};

const buildPreview = (message: InboxMessage | null) => {
  if (!message) {
    return 'Sem mensagens ainda';
  }

  if (message.body?.trim()) {
    return message.body;
  }

  switch (message.type) {
    case 'image':
      return 'Imagem';
    case 'video':
      return 'Vídeo';
    case 'audio':
      return 'Áudio';
    case 'document':
      return 'Documento';
    case 'sticker':
      return 'Sticker';
    default:
      return 'Mensagem';
  }
};

const buildMessageLabel = (message: InboxMessage) => {
  if (message.body?.trim()) {
    return message.body;
  }

  switch (message.type) {
    case 'image':
      return 'Imagem recebida';
    case 'video':
      return 'Vídeo recebido';
    case 'audio':
      return 'Áudio recebido';
    case 'document':
      return message.fileName || 'Documento recebido';
    case 'sticker':
      return 'Sticker recebido';
    default:
      return 'Mensagem sem prévia';
  }
};

const initialsFromName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) {
    return 'WA';
  }

  return parts.map((part) => part[0]?.toUpperCase() || '').join('');
};

const extractDigitsFromWhatsAppId = (value?: string | null) => {
  if (!value) {
    return '';
  }

  return value.replace(/@.+$/, '').replace(/\D/g, '');
};

const normalizeBrazilianPhone = (value?: string | null) => {
  const rawValue = String(value || '').trim().toLowerCase();

  if (!rawValue || rawValue.endsWith('@lid')) {
    return null;
  }

  const digits = extractDigitsFromWhatsAppId(rawValue);
  if (digits.length === 10 || digits.length === 11) {
    return digits;
  }

  if (digits.startsWith('55')) {
    const withoutCountryCode = digits.slice(2);
    if (withoutCountryCode.length === 10 || withoutCountryCode.length === 11) {
      return withoutCountryCode;
    }
  }

  return null;
};

const formatWhatsAppPhoneFallback = (value?: string | null) => {
  const localDigits = normalizeBrazilianPhone(value);

  if (!localDigits) {
    return 'Sem nome';
  }

  if (localDigits.length === 11) {
    return `(${localDigits.slice(0, 2)})${localDigits.slice(2, 7)}-${localDigits.slice(7)}`;
  }

  if (localDigits.length === 10) {
    return `(${localDigits.slice(0, 2)})${localDigits.slice(2, 6)}-${localDigits.slice(6)}`;
  }

  return 'Sem nome';
};

const isWhatsAppIdentifier = (value?: string | null) => {
  if (!value) {
    return false;
  }

  return /@(?:c\.us|g\.us|s\.whatsapp\.net|lid)$/i.test(value.trim());
};

const looksLikePhoneLabel = (value?: string | null) => {
  if (!value) {
    return false;
  }

  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 13;
};

const isGroupChat = (chat: InboxChatSummary) => {
  if (chat.isGroup) {
    return true;
  }

  if (chat.id?.includes('@g.us')) {
    return true;
  }

  if (Array.isArray(chat.participants) && chat.participants.length > 1) {
    return true;
  }

  return false;
};

const hasDirectPhoneIdentity = (chat: InboxChatSummary) => {
  return Boolean(normalizeBrazilianPhone(chat.phone || chat.id || chat.name));
};

const isRenderableDirectChat = (chat: InboxChatSummary) => !isGroupChat(chat) && hasDirectPhoneIdentity(chat);

const getDisplayChatName = (chat: InboxChatSummary | null) => {
  if (!chat) {
    return 'Conversa';
  }

  const trimmedName = chat.name?.trim();
  if (trimmedName && !isWhatsAppIdentifier(trimmedName)) {
    return trimmedName;
  }

  return formatWhatsAppPhoneFallback(chat.phone || chat.id || chat.name);
};

const getSidebarChatLabel = (chat: InboxChatSummary | null) => {
  if (!chat) {
    return 'Conversa';
  }

  const trimmedName = chat.name?.trim();
  if (trimmedName && !isWhatsAppIdentifier(trimmedName) && !looksLikePhoneLabel(trimmedName)) {
    return trimmedName;
  }

  return formatWhatsAppPhoneFallback(chat.phone || chat.id || chat.name);
};

const getMessageAuthorLabel = (
  message: InboxMessage,
  chat: InboxChatSummary | null,
) => {
  if (message.fromMe) {
    return 'Você';
  }

  const rawAuthor = message.from?.trim();
  if (chat?.isGroup && rawAuthor && rawAuthor !== message.chatId) {
    return rawAuthor;
  }

  const chatName = chat?.name?.trim();
  if (chatName) {
    return chatName;
  }

  if (rawAuthor && rawAuthor !== message.chatId) {
    return rawAuthor;
  }

  return getDisplayChatName(chat);
};

const escapeHtml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

export function WhatsAppChatsPage() {
  const { selectedTenantId, loading: tenantLoading } = useTenant();
  const navigate = useNavigate();
  const { capabilities } = useRuntimeCapabilities(selectedTenantId);
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [selectedSessionName, setSelectedSessionName] = useState('');
  const [chats, setChats] = useState<InboxChatSummary[]>([]);
  const [selectedChatId, setSelectedChatId] = useState('');
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [messagesByChatIdentity, setMessagesByChatIdentity] = useState<Record<string, InboxMessage[]>>({});
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [mockMessagesByChatId, setMockMessagesByChatId] = useState<Record<string, InboxMessage[]>>({});
  const [autoMockFallback, setAutoMockFallback] = useState(false);
  const [pendingMediaFile, setPendingMediaFile] = useState<File | null>(null);
  const [pendingMediaType, setPendingMediaType] = useState<InboxMediaType | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; alt: string } | null>(null);
  const [isMediaMenuOpen, setIsMediaMenuOpen] = useState(false);
  const [isQuickTextsOpen, setIsQuickTextsOpen] = useState(false);
  const [quickTextFilter, setQuickTextFilter] = useState('');
  const [quickTextCategory, setQuickTextCategory] = useState('all');
  const [quickTexts, setQuickTexts] = useState<typeof DEFAULT_QUICK_TEXTS>(() => {
    try {
      const stored = localStorage.getItem(QUICK_TEXTS_STORAGE_KEY);
      if (stored) return JSON.parse(stored) as typeof DEFAULT_QUICK_TEXTS;
    } catch {
      // Ignore invalid local data and fall back to defaults.
    }
    return DEFAULT_QUICK_TEXTS;
  });
  const [quickTextModal, setQuickTextModal] = useState<QuickTextModalState | null>(null);
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const mediaMenuRef = useRef<HTMLDivElement | null>(null);
  const [isNarrowLayout, setIsNarrowLayout] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(max-width: 1024px)').matches;
  });
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(max-width: 1024px), (max-height: 640px)').matches;
  });
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const skipNextChatsLoadRef = useRef(false);
  const skipNextMessagesLoadRef = useRef(false);
  const mediaProxyUrlsRef = useRef<Record<string, string>>({});
  const mediaProxyPromisesRef = useRef<Record<string, Promise<string>>>({});
  const firstChainChatIdRef = useRef<string | null>(null);
  const initialLoadDoneRef = useRef(false);
  const selectedSessionNameRef = useRef('');
  const selectedChatIdRef = useRef('');
  const selectedChatIdentityRef = useRef('');
  const searchRef = useRef('');

  const detectMediaTypeFromFile = useCallback((file: File): InboxMediaType => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document';
  }, []);

  const clearPendingMedia = useCallback(() => {
    setPendingMediaFile(null);
    setPendingMediaType(null);
    if (mediaInputRef.current) {
      mediaInputRef.current.value = '';
    }
  }, []);

  const openMediaPicker = useCallback((type: InboxMediaType) => {
    setPendingMediaType(type);
    setIsMediaMenuOpen(false);
    if (!mediaInputRef.current) return;
    if (type === 'image') {
      mediaInputRef.current.accept = 'image/*';
    } else if (type === 'audio') {
      mediaInputRef.current.accept = 'audio/*';
    } else {
      mediaInputRef.current.accept = '*/*';
    }
    mediaInputRef.current.click();
  }, []);

  const toggleMediaMenu = useCallback(() => setIsMediaMenuOpen((prev) => !prev), []);

  const mockModeEnabled = (() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    return params.get('mock') === '1' || window.localStorage.getItem('whatsapp_chats_mock') === '1';
  })();

  const simulationEnabled = mockModeEnabled || autoMockFallback;

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(buildApiUrl(url), {
      ...options,
      headers
    });
  };

  const releaseResolvedMediaUrls = useCallback(() => {
    Object.values(mediaProxyUrlsRef.current).forEach((objectUrl) => {
      if (objectUrl.startsWith('blob:')) {
        URL.revokeObjectURL(objectUrl);
      }
    });
    mediaProxyUrlsRef.current = {};
    mediaProxyPromisesRef.current = {};
  }, []);

  const resolveMediaUrlForDisplay = useCallback(async (rawUrl: string | null): Promise<string | null> => {
    if (!rawUrl) {
      return null;
    }

    if (/^(blob:|data:)/i.test(rawUrl)) {
      return rawUrl;
    }

    if (mediaProxyUrlsRef.current[rawUrl]) {
      return mediaProxyUrlsRef.current[rawUrl];
    }

    if (mediaProxyPromisesRef.current[rawUrl]) {
      return mediaProxyPromisesRef.current[rawUrl];
    }

    const pendingRequest = (async () => {
      const response = await authenticatedFetch(`/waha/media-proxy?url=${encodeURIComponent(rawUrl)}`, {
        headers: {
          Accept: '*/*',
        },
      });

      if (!response.ok) {
        throw new Error(`Falha ao carregar mídia (${response.status})`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      mediaProxyUrlsRef.current[rawUrl] = objectUrl;
      return objectUrl;
    })();

    mediaProxyPromisesRef.current[rawUrl] = pendingRequest;

    try {
      return await pendingRequest;
    } finally {
      delete mediaProxyPromisesRef.current[rawUrl];
    }
  }, [authenticatedFetch]);

  const hydrateMessagesMediaUrls = useCallback(async (incomingMessages: InboxMessage[]): Promise<InboxMessage[]> => {
    if (incomingMessages.length === 0) {
      return incomingMessages;
    }

    const resolvedEntries = await Promise.all(
      incomingMessages.map(async (message) => {
        if (!message.hasMedia || !message.mediaUrl) {
          return [message.id, null] as const;
        }

        try {
          const resolvedMediaUrl = await resolveMediaUrlForDisplay(message.mediaUrl);
          return [message.id, resolvedMediaUrl] as const;
        } catch (error) {
          console.error('Erro ao resolver mídia do chat:', error);
          return [message.id, message.mediaUrl] as const;
        }
      })
    );

    const resolvedMap = new Map<string, string | null>(resolvedEntries);

    return incomingMessages.map((message) => {
      const resolvedMediaUrl = resolvedMap.get(message.id);
      return resolvedMediaUrl
        ? { ...message, mediaUrl: resolvedMediaUrl }
        : message;
    });
  }, [resolveMediaUrlForDisplay]);

  const workingSessions = sessions.filter(
    (session) => session.status === 'WORKING' && session.provider === 'EVOLUTION'
  );
  const directChats = consolidateChatSummaries(chats.filter(isRenderableDirectChat));
  const selectedSession = workingSessions.find((session) => session.name === selectedSessionName) || null;
  const selectedChatIdentity = getChatIdentityFromValue(selectedChatId);
  const selectedChat = directChats.find((chat) => getChatIdentity(chat) === selectedChatIdentity) || null;
  const sortedChats = [...directChats].sort(compareChatsByRecency);
  const orderedMessages = [...messages].sort((a, b) => a.timestamp - b.timestamp);
  const selectedChatMessagesCount = messages.length;
  const showChatPanel = !isNarrowLayout || Boolean(selectedChatId);
  const showChatList = !isNarrowLayout || !selectedChatId;
  const inboxPollInterval = capabilities.recommendations.inboxPollingIntervalMs;
  const activeChatPollInterval = typeof document !== 'undefined' && document.visibilityState === 'visible'
    ? Math.max(1500, Math.min(inboxPollInterval, 2500))
    : Math.max(inboxPollInterval, 5000);

  useEffect(() => () => {
    releaseResolvedMediaUrls();
  }, [releaseResolvedMediaUrls]);

  const loadSessions = async (showLoader = true) => {
    if (simulationEnabled) {
      const mockSession: WhatsAppSession = {
        name: MOCK_CHAT_SESSION,
        displayName: 'Mock Evolution',
        status: 'WORKING',
        provider: 'EVOLUTION',
        me: {
          id: 'mock@wa',
          pushName: 'Ambiente de Simulação'
        }
      };

      setSessions([mockSession]);
      setSelectedSessionName(MOCK_CHAT_SESSION);
      return;
    }

    try {
      if (showLoader) {
        setLoadingSessions(true);
      }
      const response = await authenticatedFetch('/waha/sessions');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const nextSessions = Array.isArray(data) ? data : [];
      setSessions(nextSessions);

      const nextWorkingSessions = nextSessions.filter(
        (session: WhatsAppSession) => session.status === 'WORKING' && session.provider === 'EVOLUTION'
      );

      if (showLoader && !initialLoadDoneRef.current) {
        // Initial page load: chain sessions → chats → messages in one async flow,
        // keeping loadingSessions=true the whole time so there is a single spinner.
        initialLoadDoneRef.current = true;
        const resolvedSessionName = (selectedSessionName && nextWorkingSessions.some(
          (session: WhatsAppSession) => session.name === selectedSessionName
        )) ? selectedSessionName : (nextWorkingSessions[0]?.name || '');

        setSelectedSessionName(resolvedSessionName);

        if (resolvedSessionName) {
          // loadChats with showLoader=false so it doesn't toggle its own spinner;
          // returns the firstChatId and any inline mockMessages to avoid stale state.
          const { firstChatId, mockMessages } = await loadChats(false, resolvedSessionName);
          skipNextChatsLoadRef.current = true;
          if (firstChatId) {
            skipNextMessagesLoadRef.current = true;
            await loadMessages(firstChatId, false, resolvedSessionName, mockMessages);
          }
        }
      } else {
        setSelectedSessionName((currentSessionName) => {
          if (currentSessionName && nextWorkingSessions.some((session: WhatsAppSession) => session.name === currentSessionName)) {
            return currentSessionName;
          }

          return nextWorkingSessions[0]?.name || '';
        });
      }
    } catch (error) {
      console.error('Erro ao carregar sessões para a inbox:', error);
      if (showLoader) {
        toast.error('Erro ao carregar sessões WhatsApp');
      }
    } finally {
      if (showLoader) {
        setLoadingSessions(false);
      }
    }
  };

  // Returns { firstChatId, mockMessages } so the initial chain can pass data forward
  // without relying on React state updates that haven't committed yet.
  const loadChats = async (
    showLoader = true,
    sessionOverride?: string
  ): Promise<{ firstChatId: string | null; mockMessages: Record<string, InboxMessage[]> | null }> => {
    const sessionName = sessionOverride ?? selectedSessionName;

    if (simulationEnabled) {
      const mockData = buildMockData();
      const consolidatedMockChats = consolidateChatSummaries(mockData.chats);
      setChats(consolidatedMockChats);
      setMockMessagesByChatId(mockData.messagesByChatId);
      const mockFirstId = consolidatedMockChats[0]?.id || null;
      firstChainChatIdRef.current = mockFirstId;
      setSelectedChatId((currentChatId) => {
        const currentIdentity = getChatIdentityFromValue(currentChatId);
        const matchingChat = consolidatedMockChats.find((chat) => getChatIdentity(chat) === currentIdentity);
        if (matchingChat) {
          return matchingChat.id;
        }

        if (currentChatId && consolidatedMockChats.some((chat) => chat.id === currentChatId)) {
          return currentChatId;
        }
        return mockFirstId || '';
      });
      return { firstChatId: mockFirstId, mockMessages: mockData.messagesByChatId };
    }

    if (!sessionName) {
      setChats([]);
      setSelectedChatId('');
      firstChainChatIdRef.current = null;
      return { firstChatId: null, mockMessages: null };
    }

    try {
      if (showLoader) {
        setLoadingChats(true);
      }

      const query = new URLSearchParams({
        limit: '120',
        ...(search.trim() ? { search: search.trim() } : {})
      });

      const response = await authenticatedFetch(`/waha/sessions/${sessionName}/chats?${query.toString()}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro ao carregar conversas' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const nextChats = consolidateChatSummaries(Array.isArray(data?.chats) ? data.chats : []);
      if (nextChats.length === 0 && chats.length > 0 && !search.trim()) {
        return {
          firstChatId: selectedChatIdRef.current || chats[0]?.id || null,
          mockMessages: null
        };
      }
      setChats(nextChats);

      const firstChatId = nextChats[0]?.id || null;
      firstChainChatIdRef.current = firstChatId;
      setSelectedChatId((currentChatId) => {
        const currentIdentity = getChatIdentityFromValue(currentChatId);
        const matchingChat = nextChats.find((chat: InboxChatSummary) => getChatIdentity(chat) === currentIdentity);
        if (matchingChat) {
          return matchingChat.id;
        }

        return firstChatId || '';
      });

      return { firstChatId, mockMessages: null };
    } catch (error) {
      console.error('Erro ao carregar chats:', error);
      firstChainChatIdRef.current = null;

      const errorMessage = error instanceof Error ? error.message : '';
      const shouldEnableAutoMock = !simulationEnabled && errorMessage.includes('Evolution API indisponível');

      if (shouldEnableAutoMock) {
        setAutoMockFallback(true);
        const mockData = buildMockData();
        const hasSessionSelected = Boolean(sessionName);

        if (!hasSessionSelected) {
          setSessions([{
            name: MOCK_CHAT_SESSION,
            displayName: 'Mock Evolution',
            status: 'WORKING',
            provider: 'EVOLUTION',
            me: {
              id: 'mock@wa',
              pushName: 'Ambiente de Simulação'
            }
          }]);
          setSelectedSessionName(MOCK_CHAT_SESSION);
        }

        const consolidatedMockChats = consolidateChatSummaries(mockData.chats);
        setChats(consolidatedMockChats);
        setMockMessagesByChatId(mockData.messagesByChatId);
        const mockFallbackId = consolidatedMockChats[0]?.id || null;
        firstChainChatIdRef.current = mockFallbackId;
        setSelectedChatId(mockFallbackId || '');
        toast.success('Modo simulação ativado: exibindo 2 conversas de exemplo');
        return { firstChatId: mockFallbackId, mockMessages: mockData.messagesByChatId };
      }

      if (showLoader) {
        toast.error(error instanceof Error ? error.message : 'Erro ao carregar conversas');
      }

      return { firstChatId: null, mockMessages: null };
    } finally {
      if (showLoader) {
        setLoadingChats(false);
      }
    }
  };

  const loadMessages = async (
    chatId: string,
    showLoader = true,
    sessionOverride?: string,
    mockMessagesOverride?: Record<string, InboxMessage[]> | null
  ) => {
    const sessionName = sessionOverride ?? selectedSessionName;

    if (simulationEnabled) {
      // Use inline override when called from chain to avoid stale React state
      const source = mockMessagesOverride ?? mockMessagesByChatId;
      const nextMessages = mergeMessageLists([], source[chatId] || []);
      setMessages(nextMessages);
      setMessagesByChatIdentity((currentMap) => ({
        ...currentMap,
        [getMessageCacheKey(chatId)]: nextMessages,
      }));
      return;
    }

    if (!sessionName || !chatId) {
      setMessages([]);
      return;
    }

    try {
      if (showLoader) {
        setLoadingMessages(true);
      }

      const response = await authenticatedFetch(`/waha/sessions/${sessionName}/chats/${encodeURIComponent(chatId)}/messages?limit=80`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro ao carregar mensagens' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const normalizedMessages = mergeMessageLists([], Array.isArray(data?.messages) ? data.messages : []);
      const nextMessages = await hydrateMessagesMediaUrls(normalizedMessages);
      setMessages(nextMessages);
      setMessagesByChatIdentity((currentMap) => ({
        ...currentMap,
        [getMessageCacheKey(chatId)]: nextMessages,
      }));
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      if (showLoader) {
        toast.error(error instanceof Error ? error.message : 'Erro ao carregar mensagens');
      }
    } finally {
      if (showLoader) {
        setLoadingMessages(false);
      }
    }
  };

  const uploadMediaForChat = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};
    if (token) (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    if (selectedTenantId) (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    const response = await fetch(buildApiUrl('/media/upload'), { method: 'POST', headers, body: formData });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Erro no upload' }));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    const data = await response.json();
    const url = data.url || data.mediaUrl || data.fileUrl;
    if (!url) throw new Error('URL de mídia não retornada pelo servidor');
    return url as string;
  };

  const markSelectedChatAsRead = async (chatId: string) => {
    const chatIdentity = getChatIdentityFromValue(chatId);
    if (simulationEnabled) {
      setChats((currentChats) => currentChats.map((chat) => (
        getChatIdentity(chat) === chatIdentity
          ? { ...chat, unreadCount: 0 }
          : chat
      )));
      return;
    }

    if (!selectedSessionName || !chatId) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/waha/sessions/${selectedSessionName}/chats/${encodeURIComponent(chatId)}/read`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setChats((currentChats) => currentChats.map((chat) => (
        getChatIdentity(chat) === chatIdentity
          ? { ...chat, unreadCount: 0 }
          : chat
      )));
    } catch (error) {
      console.error('Erro ao marcar chat como lido:', error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openChat = useCallback(async (chat: InboxChatSummary) => {
    const nextChatId = chat.id;
    const cacheKey = getMessageCacheKey(nextChatId);
    const cachedMessages = messagesByChatIdentity[cacheKey];

    skipNextMessagesLoadRef.current = true;
    setSelectedChatId(nextChatId);

    if (cachedMessages && cachedMessages.length > 0) {
      setMessages(cachedMessages);
    } else {
      setMessages([]);
    }

    await markSelectedChatAsRead(nextChatId);
    await loadMessages(nextChatId, !cachedMessages || cachedMessages.length === 0);
  }, [loadMessages, markSelectedChatAsRead, messagesByChatIdentity]);

  const sendMessage = async () => {
    const hasDraft = draft.trim().length > 0;
    const hasMedia = Boolean(pendingMediaFile && pendingMediaType);
    if (!selectedSessionName || !selectedChatId || (!hasDraft && !hasMedia) || sendingMessage) {
      return;
    }

    const text = draft.trim();
    setSendingMessage(true);

    if (simulationEnabled) {
      const timestamp = Math.floor(Date.now() / 1000);
      const newMessage: InboxMessage = {
        id: `mock-out-${timestamp}`,
        chatId: selectedChatId,
        from: 'Sistema',
        to: selectedChatId,
        fromMe: true,
        body: text,
        timestamp,
        type: 'text',
        hasMedia: false,
        mediaUrl: null,
        mimeType: null,
        fileName: null,
        ack: null,
        ackName: 'Enviada',
        quotedMessageId: null
      };

      setMockMessagesByChatId((currentMap) => {
        const chatMessages = currentMap[selectedChatId] || [];
        const nextMessages = [...chatMessages, newMessage];
        const nextMap = { ...currentMap, [selectedChatId]: nextMessages };
        setMessages(nextMessages);
        return nextMap;
      });

      setChats((currentChats) => {
        const next = currentChats.map((chat) => (
          chat.id === selectedChatId
            ? {
                ...chat,
                unreadCount: 0,
                lastActivityTimestamp: timestamp,
                lastMessage: newMessage
              }
            : chat
        ));

        return next.sort(compareChatsByRecency);
      });

      setDraft('');
      setSendingMessage(false);
      return;
    }

    try {
      const optimisticMessageId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimisticTimestamp = Math.floor(Date.now() / 1000);
      const optimisticMessage: InboxMessage = {
        id: optimisticMessageId,
        chatId: selectedChatId,
        from: null,
        to: selectedChatId,
        fromMe: true,
        body: text || (pendingMediaType ? pendingMediaType.toUpperCase() : ''),
        timestamp: optimisticTimestamp,
        type: pendingMediaType || 'text',
        hasMedia: hasMedia,
        mediaUrl: pendingMediaFile ? URL.createObjectURL(pendingMediaFile) : null,
        mimeType: pendingMediaFile?.type || null,
        fileName: pendingMediaFile?.name || null,
        ack: null,
        ackName: 'Enviando...',
        quotedMessageId: null,
        optimistic: true,
      };

      setMessages((currentMessages) => mergeMessageLists(currentMessages, [optimisticMessage]));
      setMessagesByChatIdentity((currentMap) => mergeMessagesIntoCache(currentMap, selectedChatId, [optimisticMessage]));
      setChats((currentChats) => applyMessagesToChatList(currentChats, [optimisticMessage], selectedChatIdentityRef.current));

      let body: Record<string, unknown>;
      if (hasMedia && pendingMediaFile && pendingMediaType) {
        const mediaUrl = await uploadMediaForChat(pendingMediaFile);
        body = {
          mediaType: pendingMediaType,
          mediaUrl,
          caption: text || undefined,
          fileName: pendingMediaFile.name,
          mimeType: pendingMediaFile.type || undefined
        };
      } else {
        body = { text };
      }

      const response = await authenticatedFetch(`/waha/sessions/${selectedSessionName}/chats/${encodeURIComponent(selectedChatId)}/messages`, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro ao enviar mensagem' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const responseData = await response.json();
      const persistedMessage = responseData?.result?.persistedMessage as InboxMessage | undefined;

      if (persistedMessage) {
        if (optimisticMessage.mediaUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(optimisticMessage.mediaUrl);
        }
        setMessages((currentMessages) => replaceOptimisticMessage(currentMessages, optimisticMessageId, persistedMessage));
        setMessagesByChatIdentity((currentMap) => ({
          ...currentMap,
          [getMessageCacheKey(selectedChatId)]: replaceOptimisticMessage(
            currentMap[getMessageCacheKey(selectedChatId)] || [],
            optimisticMessageId,
            persistedMessage
          ),
        }));
        setChats((currentChats) => applyMessagesToChatList(currentChats, [persistedMessage], selectedChatIdentityRef.current));
      } else {
        setMessages((currentMessages) => currentMessages.filter((message) => message.id !== optimisticMessageId));
        setMessagesByChatIdentity((currentMap) => ({
          ...currentMap,
          [getMessageCacheKey(selectedChatId)]: (currentMap[getMessageCacheKey(selectedChatId)] || []).filter(
            (message) => message.id !== optimisticMessageId
          ),
        }));
      }

      setDraft('');
      clearPendingMedia();
      sessionStorage.setItem('certification-campaigns-cache-invalid', 'true');
      await Promise.all([
        loadMessages(selectedChatId, false),
        loadChats(false)
      ]);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      const optimisticBlobUrl = messages.find((message) => message.optimistic)?.mediaUrl;
      if (optimisticBlobUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(optimisticBlobUrl);
      }
      setMessages((currentMessages) => currentMessages.filter((message) => !message.optimistic));
      setMessagesByChatIdentity((currentMap) => ({
        ...currentMap,
        [getMessageCacheKey(selectedChatId)]: (currentMap[getMessageCacheKey(selectedChatId)] || []).filter(
          (message) => !message.optimistic
        ),
      }));
      void loadChats(false);
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const openCertificateFromChat = async () => {
    if (!selectedSessionName || !selectedChat) {
      return;
    }

    try {
      const response = await authenticatedFetch(
        `/waha/sessions/${selectedSessionName}/chats/${encodeURIComponent(selectedChat.id)}/certificate-context`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const context = data?.context as ChatCertificateContext | null;

      navigate('/certificacao', {
        state: {
          fromChat: {
            contactName: context?.identifiedRecipientName || context?.contactName || selectedChat.name,
            contactPhone: context?.contactPhone || selectedChat.phone || selectedChat.id.replace(/@.+$/, ''),
            contactEmail: context?.contactEmail || null,
            contactDocument: context?.contactDocument || null,
            contactAddress: context?.contactAddress || context?.mandateAddress || null,
            contactProcess: context?.contactProcess || context?.mandateCaseNumber || null,
            contactMandate: context?.contactMandate || context?.mandateType || null,
            contactCategory: context?.contactCategory || null,
            contactNotes: context?.contactNotes || null,
            mandateId: context?.mandateId || null,
            mandateRecipientName: context?.mandateRecipientName || null,
            mandateAddress: context?.mandateAddress || null,
            mandateStatus: context?.mandateStatus || null,
            mandateCaseNumber: context?.mandateCaseNumber || null,
            mandateType: context?.mandateType || null,
            sentAt: context?.sentAt || null,
            createdAt: context?.createdAt || null,
            conversationStartedAt: context?.conversationStartedAt || null,
            conversationLastMessageAt: context?.conversationLastMessageAt || null,
            conversationMessageCount: context?.conversationMessageCount || 0,
            conversationSummary: context?.conversationSummary || null,
            conversationTranscript: context?.conversationTranscript || null,
            campaignId: context?.campaignId || null,
            campaignName: context?.campaignName || null,
            campaignMessageId: context?.campaignMessageId || null,
          }
        }
      });
    } catch (error) {
      console.error('Erro ao carregar contexto da certidão pelo chat:', error);
      navigate('/certificacao', {
        state: {
          fromChat: {
            contactName: selectedChat.name,
            contactPhone: selectedChat.phone || selectedChat.id.replace(/@.+$/, ''),
          }
        }
      });
    }
  };

  const handlePrintConversation = () => {
    if (!selectedChat || messages.length === 0) {
      toast.error('Selecione uma conversa com mensagens para imprimir');
      return;
    }

    const dateLabel = new Date().toLocaleString('pt-BR');
    const chatLabel = getDisplayChatName(selectedChat);
    const chatInitials = initialsFromName(selectedChat.name);

    const messageHtml = messages.map((message) => {
      const bubbleClass = message.fromMe ? 'from-me' : 'from-contact';
      const author = message.fromMe ? 'Você' : (message.from || selectedChat.name);
      const body = escapeHtml(buildMessageLabel(message));
      const time = escapeHtml(formatMessageTime(message.timestamp));
      const status = message.fromMe ? escapeHtml(message.ackName || '✓✓') : '';

      return `
        <div class="msg-row ${bubbleClass}">
          <div class="msg-bubble ${bubbleClass}">
            <div class="msg-author">${escapeHtml(author)}</div>
            <div class="msg-body">${body.replace(/\n/g, '<br/>')}</div>
            <div class="msg-meta">${time}${status ? ` · ${status}` : ''}</div>
          </div>
        </div>
      `;
    }).join('');

    const printWindow = window.open('', '_blank', 'width=980,height=720');
    if (!printWindow) {
      toast.error('Não foi possível abrir a janela de impressão');
      return;
    }

    const title = `Conversa - ${selectedChat.name}`;
    const html = `
      <!doctype html>
      <html lang="pt-BR">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>${escapeHtml(title)}</title>
        <style>
          @page { size: A4; margin: 14mm; }
          :root {
            --wa-green-900: #075e54;
            --wa-green-800: #0b7d6f;
            --wa-green-700: #128c7e;
            --wa-green-600: #00a884;
            --wa-green-500: #25d366;
            --wa-green-100: #dcf8c6;
            --wa-green-050: #ecfff2;
          }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
            background: linear-gradient(180deg, #073f38 0%, #0b141a 38%);
            color: #111b21;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .sheet {
            max-width: 860px;
            margin: 0 auto;
            background: #111b21;
            border: 1px solid #1f2c33;
            border-radius: 14px;
            overflow: hidden;
            box-shadow: 0 16px 40px rgba(0,0,0,0.35);
          }
          .header {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 14px 18px;
            border-bottom: 1px solid #2a3942;
            background: linear-gradient(90deg, var(--wa-green-900), var(--wa-green-700) 68%, var(--wa-green-600));
            color: #e9edef;
          }
          .avatar {
            width: 42px;
            height: 42px;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.24);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 0.03em;
            color: #ffffff;
            border: 1px solid rgba(255, 255, 255, 0.26);
          }
          .title-wrap { min-width: 0; }
          .title {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.2;
            color: #e9edef;
          }
          .subtitle {
            margin: 4px 0 0;
            font-size: 11px;
            color: rgba(255, 255, 255, 0.86);
          }
          .header-icons {
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 8px;
            color: rgba(255, 255, 255, 0.8);
            font-size: 12px;
          }
          .messages {
            padding: 18px;
            background-color: #e8f5ec;
            background-image:
              radial-gradient(rgba(18,140,126,0.10) 0.6px, transparent 0.6px),
              radial-gradient(rgba(0,168,132,0.08) 0.6px, transparent 0.6px);
            background-position: 0 0, 9px 9px;
            background-size: 18px 18px;
            min-height: 320px;
          }
          .day-separator {
            width: fit-content;
            margin: 2px auto 14px;
            padding: 4px 10px;
            border-radius: 999px;
            background: #d4f7e0;
            color: #0c5f53;
            font-size: 11px;
            box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08);
            border: 1px solid #b6ebcb;
          }
          .msg-row {
            display: flex;
            margin-bottom: 10px;
            page-break-inside: avoid;
          }
          .msg-row.from-me { justify-content: flex-end; }
          .msg-row.from-contact { justify-content: flex-start; }
          .msg-bubble {
            position: relative;
            max-width: 76%;
            border-radius: 10px;
            padding: 8px 10px 6px;
            border: 1px solid rgba(0, 0, 0, 0.04);
            box-shadow: 0 1px 0 rgba(11, 20, 26, 0.13);
          }
          .msg-bubble.from-me {
            background: var(--wa-green-100);
            border-color: #bfe7a5;
          }
          .msg-bubble.from-contact {
            background: var(--wa-green-050);
            border-color: #d7f0de;
          }
          .msg-bubble.from-me::after {
            content: '';
            position: absolute;
            right: -6px;
            top: 0;
            width: 10px;
            height: 10px;
            background: linear-gradient(135deg, var(--wa-green-100) 0%, var(--wa-green-100) 50%, transparent 51%, transparent 100%);
          }
          .msg-bubble.from-contact::after {
            content: '';
            position: absolute;
            left: -6px;
            top: 0;
            width: 10px;
            height: 10px;
            background: linear-gradient(225deg, var(--wa-green-050) 0%, var(--wa-green-050) 50%, transparent 51%, transparent 100%);
          }
          .msg-author {
            font-size: 10px;
            font-weight: 700;
            margin-bottom: 4px;
            color: #667781;
          }
          .msg-body {
            font-size: 13px;
            line-height: 1.45;
            white-space: normal;
            word-break: break-word;
          }
          .msg-meta {
            margin-top: 4px;
            font-size: 11px;
            color: #667781;
            text-align: right;
          }
          .footer-note {
            margin-top: 12px;
            text-align: center;
            color: #0f7666;
            font-size: 10px;
          }
        </style>
      </head>
      <body>
        <div class="sheet">
          <div class="header">
            <div class="avatar">${escapeHtml(chatInitials)}</div>
            <div class="title-wrap">
              <h1 class="title">${escapeHtml(selectedChat.name)}</h1>
              <p class="subtitle">${escapeHtml(chatLabel)} · Exportado em ${escapeHtml(dateLabel)}</p>
            </div>
            <div class="header-icons">📞 🎥 ⋮</div>
          </div>
          <div class="messages">
            <div class="day-separator">Hoje</div>
            ${messageHtml}
            <div class="footer-note">Exportação da conversa em formato visual WhatsApp</div>
          </div>
        </div>
        <script>
          window.onload = () => {
            window.focus();
            window.print();
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const saveQuickTexts = (newTexts: typeof DEFAULT_QUICK_TEXTS) => {
    setQuickTexts(newTexts);
    localStorage.setItem(QUICK_TEXTS_STORAGE_KEY, JSON.stringify(newTexts));
  };

  const openQuickTextCreate = () => {
    setQuickTextModal({ mode: 'create', category: quickTexts[0]?.category || '', label: '', text: '' });
  };

  const openQuickTextEdit = (category: string, item: { id: string; label: string; text: string }) => {
    setQuickTextModal({ mode: 'edit', originalId: item.id, originalCategory: category, category, label: item.label, text: item.text });
  };

  const deleteQuickText = (category: string, itemId: string) => {
    const next = quickTexts
      .map((g) => g.category === category ? { ...g, items: g.items.filter((i) => i.id !== itemId) } : g)
      .filter((g) => g.items.length > 0);
    saveQuickTexts(next);
    toast.success('Resposta removida');
  };

  const submitQuickTextModal = () => {
    if (!quickTextModal) return;
    const { mode, category, label, text, originalId, originalCategory } = quickTextModal;
    if (!category.trim() || !label.trim() || !text.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }

    let next = quickTexts.map((g) => ({ ...g, items: [...g.items] }));

    if (mode === 'edit' && originalId && originalCategory) {
      next = next
        .map((g) => ({
          ...g,
          items: g.category === originalCategory ? g.items.filter((i) => i.id !== originalId) : g.items,
        }))
        .filter((g) => g.items.length > 0);
    }

    const updatedItem = {
      id: mode === 'edit' && originalId ? originalId : `q-custom-${Date.now()}`,
      label: label.trim(),
      text: text.trim(),
    };

    const catTrimmed = category.trim();
    const existingGroup = next.find((g) => g.category === catTrimmed);
    if (existingGroup) {
      next = next.map((g) => g.category === catTrimmed ? { ...g, items: [...g.items, updatedItem] } : g);
    } else {
      next = [...next, { category: catTrimmed, items: [updatedItem] }];
    }

    saveQuickTexts(next);
    setQuickTextModal(null);
    toast.success(mode === 'edit' ? 'Resposta atualizada!' : 'Resposta criada!');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const narrowQuery = window.matchMedia('(max-width: 1024px)');
    const compactQuery = window.matchMedia('(max-width: 1024px), (max-height: 640px)');
    const handleChange = () => {
      setIsNarrowLayout(narrowQuery.matches);
      setIsCompactViewport(compactQuery.matches);
    };

    handleChange();
    if (narrowQuery.addEventListener) {
      narrowQuery.addEventListener('change', handleChange);
      compactQuery.addEventListener('change', handleChange);
    } else {
      narrowQuery.addListener(handleChange);
      compactQuery.addListener(handleChange);
    }

    return () => {
      if (narrowQuery.removeEventListener) {
        narrowQuery.removeEventListener('change', handleChange);
        compactQuery.removeEventListener('change', handleChange);
      } else {
        narrowQuery.removeListener(handleChange);
        compactQuery.removeListener(handleChange);
      }
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    selectedSessionNameRef.current = selectedSessionName;
  }, [selectedSessionName]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
  }, [selectedChatId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    selectedChatIdentityRef.current = selectedChatIdentity;
  }, [selectedChatIdentity]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    if (tenantLoading || !selectedTenantId) {
      return;
    }

    loadSessions();
  }, [selectedTenantId, tenantLoading]);

  useEffect(() => {
    if (tenantLoading || !selectedTenantId) {
      return;
    }

    const interval = window.setInterval(() => {
      loadSessions(false);
    }, Math.max(inboxPollInterval, 5000));

    return () => {
      window.clearInterval(interval);
    };
  }, [selectedTenantId, tenantLoading, inboxPollInterval]);

  useEffect(() => {
    if (tenantLoading || !selectedTenantId) {
      return;
    }

    const refreshInboxNow = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      void loadSessions(false);

      const activeSessionName = selectedSessionNameRef.current;
      if (!activeSessionName) {
        return;
      }

      void loadChats(false, activeSessionName);

      const activeChatId = selectedChatIdRef.current;
      if (activeChatId) {
        void loadMessages(activeChatId, false, activeSessionName);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshInboxNow();
      }
    };

    window.addEventListener('focus', refreshInboxNow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshInboxNow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedTenantId, tenantLoading]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const token = localStorage.getItem('auth_token');
    const websocketReady = capabilities.runtime.websocketEnabled && capabilities.runtime.websocketInitialized;

    if (!token || !websocketReady) {
      return;
    }

    let socket: SocketLike | null = null;
    let cancelled = false;

    const handleInboxMessages = (payload: { sessionName?: string; messages?: InboxMessage[] }) => {
      if (!payload?.sessionName || payload.sessionName !== selectedSessionNameRef.current) {
        return;
      }

      const incomingMessages = Array.isArray(payload.messages) ? payload.messages : [];
      if (incomingMessages.length === 0) {
        return;
      }

      void (async () => {
        const hydratedMessages = await hydrateMessagesMediaUrls(incomingMessages);

        setChats((currentChats) => applyMessagesToChatList(currentChats, hydratedMessages, selectedChatIdentityRef.current));
        setMessagesByChatIdentity((currentMap) => {
          let nextMap = currentMap;
          for (const message of hydratedMessages) {
            nextMap = mergeMessagesIntoCache(nextMap, message.chatId, [message]);
          }
          return nextMap;
        });

        const activeIdentity = selectedChatIdentityRef.current;
        const matchingMessages = hydratedMessages.filter((message) => getChatIdentityFromValue(message.chatId) === activeIdentity);
        if (matchingMessages.length > 0) {
          setMessages((currentMessages) => mergeMessageLists(currentMessages, matchingMessages));
        }
      })();
    };

    void (async () => {
      try {
        await loadSocketIoClient();
        if (cancelled || !window.io) {
          return;
        }

        socket = window.io(resolveSocketOrigin(), {
          path: '/socket.io/',
          transports: ['websocket', 'polling'],
          auth: { token },
        });

        socket.on('whatsapp_inbox_messages', handleInboxMessages);
      } catch (error) {
        console.warn('Socket da inbox não pôde ser inicializado, mantendo polling.', error);
      }
    })();

    return () => {
      cancelled = true;
      if (socket) {
        socket.off('whatsapp_inbox_messages', handleInboxMessages);
        socket.disconnect();
      }
    };
  }, [capabilities.runtime.websocketEnabled, capabilities.runtime.websocketInitialized, hydrateMessagesMediaUrls, selectedTenantId]);

  useEffect(() => {
    if (!selectedSessionName) {
      setChats([]);
      setSelectedChatId('');
      return;
    }

    if (skipNextChatsLoadRef.current) {
      skipNextChatsLoadRef.current = false;
    } else {
      loadChats();
    }

    const interval = window.setInterval(() => {
      loadChats(false);
    }, Math.max(2000, inboxPollInterval));

    return () => {
      window.clearInterval(interval);
    };
  }, [selectedSessionName, search, inboxPollInterval]);

  useEffect(() => {
    const activeChatId = selectedChat?.id || selectedChatId;

    if (!activeChatId) {
      setMessages([]);
      return;
    }

    markSelectedChatAsRead(activeChatId);

    if (skipNextMessagesLoadRef.current) {
      skipNextMessagesLoadRef.current = false;
    } else {
      loadMessages(activeChatId);
    }

    const interval = window.setInterval(() => {
      loadMessages(activeChatId, false);
    }, activeChatPollInterval);

    return () => {
      window.clearInterval(interval);
    };
  }, [selectedSessionName, selectedChatId, selectedChat?.id, activeChatPollInterval]);

  useEffect(() => {
    if (!selectedChatId) {
      return;
    }

    const matchingChat = directChats.find((chat) => getChatIdentity(chat) === selectedChatIdentity);
    if (!matchingChat) {
      setSelectedChatId('');
      setMessages([]);
      return;
    }

    if (matchingChat.id !== selectedChatId) {
      setSelectedChatId(matchingChat.id);
    }
  }, [directChats, selectedChatId, selectedChatIdentity]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  }, [orderedMessages, selectedChatId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mediaMenuRef.current && !mediaMenuRef.current.contains(event.target as Node)) {
        setIsMediaMenuOpen(false);
      }
    };
    if (isMediaMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMediaMenuOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewImage(null);
      }
    };

    if (previewImage) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [previewImage]);

  return (
    <>
      <Header
        title="Chats WhatsApp"
        subtitle={selectedSession ? `Inbox ativa em ${selectedSession.displayName || selectedSession.name}` : 'Acompanhe e responda conversas do WhatsApp'}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedSessionName}
              onChange={(event) => {
                setSelectedSessionName(event.target.value);
                setSelectedChatId('');
              }}
              className="rounded-lg border border-[#7bc6b4] bg-[#effcf6] px-3 py-2 text-sm text-[#0f3f36] shadow-sm focus:border-[#00a884] focus:outline-none focus:ring-2 focus:ring-[#b9f2df]"
              disabled={loadingSessions || workingSessions.length === 0}
            >
              {workingSessions.length === 0 ? (
                <option value="">Sem sessão Evolution ativa</option>
              ) : (
                workingSessions.map((session) => (
                  <option key={session.name} value={session.name}>
                    {session.displayName || session.name}
                  </option>
                ))
              )}
            </select>
            <button
              type="button"
              onClick={() => {
                loadSessions();
                if (selectedSessionName) {
                  loadChats(false);
                }
                if (selectedSessionName && selectedChatId) {
                  loadMessages(selectedChatId, false);
                }
              }}
              className="rounded-lg bg-[#00a884] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#0b8f76]"
            >
              Atualizar
            </button>
          </div>
        }
      />

      <div className={`${isCompactViewport ? 'p-2 sm:p-4' : 'p-4 sm:p-6'}`}>
        <div className={`mx-auto overflow-hidden rounded-[16px] border border-[#d1d7db] bg-[#e9edef] shadow-[0_20px_45px_-30px_rgba(15,23,42,0.55)] ${
          isCompactViewport
            ? 'h-[calc(100dvh-108px)] min-h-[420px]'
            : 'h-[calc(100dvh-150px)] min-h-[620px]'
        }`}>
          {loadingSessions ? (
            <div className="flex h-full items-center justify-center">
              <div className="flex items-center gap-3 text-gray-500">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-green-200 border-t-green-600"></div>
                <span>Carregando sessões conectadas...</span>
              </div>
            </div>
          ) : workingSessions.length === 0 ? (
            <div className="flex h-full items-center justify-center bg-[radial-gradient(circle_at_top_left,_rgba(22,163,74,0.12),_transparent_34%),linear-gradient(135deg,_#f8fafc,_#ecfeff)] p-6">
              <div className="max-w-lg rounded-[28px] border border-green-100 bg-white/90 p-8 text-center shadow-xl backdrop-blur">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-green-100 text-green-700">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4v-4z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">Nenhuma sessão Evolution ativa</h2>
                <p className="mt-3 text-sm leading-6 text-gray-600">
                  Para usar a inbox, o tenant precisa ter uma sessão Evolution em estado <strong>Conectado</strong>.
                  Assim que ela estiver ativa, as conversas aparecem aqui automaticamente.
                </p>
                <Link
                  to="/whatsapp"
                  className="mt-6 inline-flex items-center rounded-xl bg-green-600 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-green-700"
                >
                  Ir para conexões
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid h-full min-h-0 lg:grid-cols-[360px,minmax(0,1fr)]">
              {showChatList && (
                <aside className="flex h-full min-h-0 flex-col border-r border-[#d1d7db] bg-[#f7f9fa]">
                  <div className="border-b border-[#d1d7db] bg-[#f0f2f5] p-4">
                    <div className="mb-3 rounded-xl border border-[#d1d7db] bg-white p-3 text-[#111b21]">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#667781]">Conexão ativa</p>
                      <h2 className="mt-1 text-base font-semibold">
                        {selectedSession?.displayName || selectedSession?.name}
                      </h2>
                      <p className="mt-1 text-xs text-[#667781]">
                        {selectedSession?.me?.pushName || 'WhatsApp conectado'} · {selectedSession?.provider}
                      </p>
                    </div>
                    <label className="relative block">
                      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-[#667781]">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M16 10.5a5.5 5.5 0 11-11 0 5.5 5.5 0 0111 0z" />
                        </svg>
                      </span>
                      <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Buscar conversa, nome ou número"
                        className="w-full rounded-lg border border-[#d1d7db] bg-white py-2.5 pl-10 pr-4 text-sm text-[#111b21] placeholder:text-[#667781] focus:border-[#00a884] focus:outline-none"
                      />
                    </label>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {loadingChats ? (
                      <div className="flex h-52 items-center justify-center text-sm text-[#667781]">
                        Carregando conversas...
                      </div>
                    ) : sortedChats.length === 0 ? (
                      <div className="p-6 text-center text-sm text-[#667781]">
                        Nenhuma conversa individual encontrada para esta conexão.
                      </div>
                    ) : (
                      sortedChats.map((chat) => {
                        const isActive = getChatIdentity(chat) === selectedChatIdentity;
                        const openChatMessages = () => {
                          void openChat(chat);
                        };
                        return (
                          <button
                            key={chat.id}
                            type="button"
                            onClick={openChatMessages}
                            className={`flex w-full items-start gap-3 border-b border-[#e4e8ea] px-4 py-3.5 text-left transition-colors ${
                              isActive
                                ? 'bg-[#e9edef] shadow-[inset_3px_0_0_0_#00a884]'
                                : 'hover:bg-[#f5f7f8]'
                            }`}
                          >
                            {chat.picture ? (
                              <img
                                src={chat.picture}
                                alt={getSidebarChatLabel(chat)}
                                className="h-12 w-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#dfe5e7] text-sm font-semibold text-[#54656f]">
                                {initialsFromName(getSidebarChatLabel(chat))}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-[#111b21]">
                                    {getSidebarChatLabel(chat)}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <span className="text-[11px] font-medium text-[#667781]">
                                    {formatListTime(getChatRecencyTimestamp(chat))}
                                  </span>
                                  {chat.unreadCount > 0 && (
                                    <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-[#00a884] px-1.5 py-0.5 text-[11px] font-semibold text-white">
                                      {chat.unreadCount}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="mt-1.5 truncate text-sm text-[#667781]">
                                {chat.lastMessage?.fromMe ? 'Você: ' : ''}
                                {buildPreview(chat.lastMessage)}
                              </p>
                              <div className="mt-2">
                                <span className="inline-flex items-center rounded-full border border-[#b7d8cf] bg-[#edfdf7] px-2.5 py-1 text-[11px] font-medium text-[#0b6b58]">
                                  {isActive ? 'Mensagens abertas' : 'Abrir mensagens'}
                                </span>
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </aside>
              )}

              {showChatPanel && (
                <section className="relative flex h-full min-h-0 overflow-hidden bg-[#e8f5ec]">
                  <div className="flex min-w-0 flex-1 flex-col">
                  {selectedChat ? (
                    <>
                      <div className={`flex border-b border-[#d1d7db] bg-[#f0f2f5] px-3 py-3 sm:px-6 ${isCompactViewport ? 'flex-col gap-3' : 'items-center justify-between'}`}>
                        <div className="flex min-w-0 items-center gap-3">
                          {isNarrowLayout && (
                            <button
                              type="button"
                              onClick={() => setSelectedChatId('')}
                              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#d1d7db] text-[#54656f] hover:bg-[#e9edef] lg:hidden"
                            >
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                          )}
                          {selectedChat.picture ? (
                            <img
                              src={selectedChat.picture}
                              alt={selectedChat.name}
                              className="h-11 w-11 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#dfe5e7] text-sm font-semibold text-[#54656f]">
                              {initialsFromName(selectedChat.name)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <h2 className="truncate text-sm font-semibold text-[#111b21] sm:text-base">
                              {getDisplayChatName(selectedChat)}
                            </h2>
                            <p className="truncate text-xs text-[#667781]">
                              {selectedChat.isGroup ? 'Grupo' : 'Conversa direta'}
                            </p>
                            <p className="mt-1 truncate text-[11px] font-medium uppercase tracking-[0.12em] text-[#0b8f76]">
                              Mensagens trocadas entre as partes
                            </p>
                          </div>
                        </div>
                        <div className={`flex w-full gap-2 ${isCompactViewport ? 'flex-wrap' : 'items-center justify-end'}`}>
                          <button
                            type="button"
                            onClick={openCertificateFromChat}
                            className={`rounded-lg border border-[#00a884] bg-[#e8fff5] px-3 py-1.5 text-xs font-medium text-[#00a884] transition-colors hover:bg-[#d0f5e8] ${isCompactViewport ? 'flex-1 text-center' : ''}`}
                          >
                            Criar Certidão
                          </button>
                          <button
                            type="button"
                            onClick={handlePrintConversation}
                            disabled={messages.length === 0}
                            className={`rounded-lg border border-[#d1d7db] bg-white px-3 py-1.5 text-xs font-medium text-[#111b21] transition-colors hover:bg-[#f6f8f9] disabled:cursor-not-allowed disabled:opacity-50 ${isCompactViewport ? 'flex-1 text-center' : ''}`}
                          >
                            Imprimir / PDF
                          </button>
                          <div className="hidden rounded-full bg-[#e9edef] px-3 py-1 text-xs font-medium text-[#667781] sm:block">
                            {selectedSession?.provider}
                          </div>
                        </div>
                      </div>

                      <div
                        ref={messagesContainerRef}
                        className="flex-1 space-y-2.5 overflow-y-auto px-4 py-5 sm:px-6 bg-[#e8f5ec] bg-[radial-gradient(rgba(18,140,126,0.08)_0.6px,transparent_0.6px),radial-gradient(rgba(0,168,132,0.06)_0.6px,transparent_0.6px)] [background-position:0_0,9px_9px] [background-size:18px_18px]"
                      >
                        <div className="mb-4 flex items-center justify-between rounded-2xl border border-[#c9dbd5] bg-white/80 px-4 py-3 text-xs text-[#54656f] shadow-sm backdrop-blur-sm">
                          <span>Historico completo da conversa selecionada</span>
                          <span className="font-semibold text-[#0b6b58]">
                            {selectedChatMessagesCount} {selectedChatMessagesCount === 1 ? 'mensagem' : 'mensagens'}
                          </span>
                        </div>
                        {loadingMessages ? (
                          <div className="flex h-full items-center justify-center text-sm text-[#54656f]">
                            Carregando mensagens...
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="flex h-full items-center justify-center">
                            <div className="rounded-2xl border border-dashed border-[#b6c4c8] bg-white/85 px-6 py-4 text-center text-sm text-[#54656f] shadow-sm">
                              Nenhuma mensagem carregada para esta conversa.
                            </div>
                          </div>
                        ) : (
                          orderedMessages.map((message) => (
                            <div
                              key={message.id}
                              className={`flex ${message.fromMe ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`w-[88%] max-w-[88%] sm:w-[78%] sm:max-w-[80%] rounded-[9px] px-3 py-2 shadow-sm ${
                                  message.fromMe
                                    ? 'rounded-br-sm bg-[#dcf8c6] text-[#111b21]'
                                    : 'rounded-bl-sm bg-[#f2f7f5] text-[#111b21]'
                                }`}
                              >
                                <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#0b6b58]">
                                  {getMessageAuthorLabel(message, selectedChat)}
                                </p>
                                {message.hasMedia && message.mediaUrl && message.type === 'image' && (
                                  <button
                                    type="button"
                                    onClick={() => setPreviewImage({ url: message.mediaUrl!, alt: message.fileName || 'Imagem' })}
                                    className="mb-1 block overflow-hidden rounded-md"
                                  >
                                    <img
                                      src={message.mediaUrl}
                                      alt={message.fileName || 'Imagem'}
                                      className="max-h-[320px] w-full rounded-md object-contain transition-transform hover:scale-[1.02]"
                                    />
                                  </button>
                                )}
                                {message.hasMedia && message.mediaUrl && message.type === 'audio' && (
                                  <div className="mb-1 rounded-xl border border-[#c8d8d2] bg-white/70 p-2">
                                    <audio controls preload="metadata" src={message.mediaUrl} className="w-full" />
                                  </div>
                                )}
                                {message.hasMedia && message.mediaUrl && message.type === 'document' && (
                                  <a
                                    href={message.mediaUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mb-1 flex items-center gap-2 rounded-lg border border-[#d1d7db] bg-white/70 px-3 py-2 text-xs font-medium text-[#005c4b] hover:bg-white"
                                  >
                                    <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    {message.fileName || 'Documento'}
                                  </a>
                                )}
                                {message.hasMedia && message.mediaUrl && message.type !== 'image' && message.type !== 'audio' && message.type !== 'document' && (
                                  <a
                                    href={message.mediaUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-1 inline-flex text-xs underline text-[#005c4b]"
                                  >
                                    Abrir anexo
                                  </a>
                                )}
                                {buildMessageLabel(message) && (
                                  <p className="whitespace-pre-wrap break-words text-[13px] leading-5">
                                    {buildMessageLabel(message)}
                                  </p>
                                )}
                                <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-[#667781]">
                                  <span>{formatMessageTime(message.timestamp)}</span>
                                  {message.fromMe && message.ackName && (
                                    <span>{message.ackName}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      <div className="border-t border-[#d1d7db] bg-[#f0f2f5] px-4 py-3 sm:px-6">
                        {selectedSession?.provider === 'EVOLUTION' && selectedChat.isGroup ? (
                          <div className="rounded-2xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            Esta conversa é um grupo. A leitura está disponível, mas o envio por esta tela fica restrito para grupos em sessões Evolution.
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            {pendingMediaFile && (
                              <div className="flex items-center gap-2 rounded-lg border border-[#d1d7db] bg-white px-3 py-2 text-xs text-[#111b21]">
                                <span className="flex-1 truncate">{pendingMediaFile.name}</span>
                                <button type="button" onClick={clearPendingMedia} className="shrink-0 text-[#667781] hover:text-red-500">
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              </div>
                            )}
                            <div className={`flex gap-2 ${isCompactViewport ? 'flex-col items-stretch' : 'items-end'}`}>
                              <div ref={mediaMenuRef} className="relative shrink-0">
                                <button
                                  type="button"
                                  onClick={toggleMediaMenu}
                                  title="Anexar arquivo"
                                  className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-xl border border-[#d1d7db] bg-white text-[#54656f] transition-colors hover:bg-[#e9edef]"
                                >
                                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="7" height="7" rx="1"/>
                                    <rect x="14" y="3" width="7" height="7" rx="1"/>
                                    <rect x="3" y="14" width="7" height="7" rx="1"/>
                                    <rect x="14" y="14" width="7" height="7" rx="1"/>
                                  </svg>
                                </button>
                                {isMediaMenuOpen && (
                                  <div className="absolute bottom-full left-0 mb-2 w-44 rounded-xl border border-[#d1d7db] bg-white shadow-lg">
                                    <button type="button" onClick={() => openMediaPicker('image')} className="flex w-full items-center gap-3 px-4 py-3 text-sm text-[#111b21] hover:bg-[#f0f2f5] rounded-t-xl">
                                      <svg className="h-5 w-5 text-[#00a884]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                      Imagem
                                    </button>
                                    <button type="button" onClick={() => openMediaPicker('audio')} className="flex w-full items-center gap-3 px-4 py-3 text-sm text-[#111b21] hover:bg-[#f0f2f5]">
                                      <svg className="h-5 w-5 text-[#00a884]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" /></svg>
                                      Áudio
                                    </button>
                                    <button type="button" onClick={() => openMediaPicker('document')} className="flex w-full items-center gap-3 px-4 py-3 text-sm text-[#111b21] hover:bg-[#f0f2f5] rounded-b-xl">
                                      <svg className="h-5 w-5 text-[#00a884]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                      Documento
                                    </button>
                                  </div>
                                )}
                              </div>
                              <input
                                ref={mediaInputRef}
                                type="file"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const detectedType = pendingMediaType || detectMediaTypeFromFile(file);
                                  setPendingMediaFile(file);
                                  setPendingMediaType(detectedType);
                                }}
                              />
                              <textarea
                                value={draft}
                                onChange={(event) => setDraft(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault();
                                    sendMessage();
                                  }
                                }}
                                placeholder={pendingMediaFile ? 'Legenda (opcional)' : 'Digite uma mensagem'}
                                rows={1}
                                className="max-h-36 min-h-[44px] flex-1 resize-y rounded-xl border border-[#d1d7db] bg-white px-4 py-3 text-sm text-[#111b21] placeholder:text-[#667781] focus:border-[#00a884] focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={sendMessage}
                                disabled={(!draft.trim() && !pendingMediaFile) || sendingMessage}
                                className={`inline-flex h-[44px] items-center justify-center rounded-xl bg-[#00a884] px-5 text-sm font-medium text-white transition-colors hover:bg-[#029a79] disabled:cursor-not-allowed disabled:bg-[#9fd3c7] ${isCompactViewport ? 'w-full' : ''}`}
                              >
                                {sendingMessage ? 'Enviando...' : 'Enviar'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex h-full items-center justify-center px-6 bg-[#e8f5ec]">
                      <div className="max-w-md text-center">
                        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#dfe5e7] text-[#54656f]">
                          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 8h10M7 12h6m-8 8l-4-4m0 0l4-4m-4 4h18" />
                          </svg>
                        </div>
                        <h2 className="text-2xl font-semibold text-[#111b21]">Conversas individuais apenas</h2>
                        <p className="mt-3 text-sm leading-6 text-[#54656f]">
                          O AuxiliaJud acessa somente conversas individuais do WhatsApp. Grupos ficam ocultos nesta tela.
                        </p>
                      </div>
                    </div>
                  )}
                  </div>

                  {isCompactViewport && isQuickTextsOpen && (
                    <button
                      type="button"
                      aria-label="Fechar respostas rápidas"
                      onClick={() => setIsQuickTextsOpen(false)}
                      className="absolute inset-0 z-20 bg-black/20"
                    />
                  )}

                  {/* Barra lateral de Respostas Rápidas */}
                  <div className={`${
                    isCompactViewport
                      ? `absolute inset-y-0 right-0 z-30 border-l border-[#d1d7db] bg-white shadow-2xl transition-transform duration-300 ${isQuickTextsOpen ? 'translate-x-0 w-[min(88vw,20rem)]' : 'translate-x-full w-[min(88vw,20rem)]'}`
                      : `relative shrink-0 border-l border-[#d1d7db] bg-white transition-all duration-300 ${isQuickTextsOpen ? 'w-72' : 'w-0 border-l-0'}`
                  }`}>
                    <button
                      type="button"
                      onClick={() => setIsQuickTextsOpen((prev) => !prev)}
                      className={`absolute left-0 top-1/2 z-20 -translate-y-1/2 border border-r-0 border-[#0b8f76] bg-[#00a884] text-[11px] font-semibold uppercase tracking-[0.08em] text-white shadow-md hover:bg-[#0b8f76] ${
                        isCompactViewport
                          ? '-translate-x-full rounded-l-2xl px-2.5 py-3'
                          : '-translate-x-full rounded-l-xl px-2 py-3'
                      }`}
                      title={isQuickTextsOpen ? 'Fechar envios rápidos' : 'Abrir envios rápidos'}
                    >
                      {isQuickTextsOpen ? (
                        <span className="text-base leading-none">‹</span>
                      ) : (
                        <span className="[writing-mode:vertical-rl]">Rapidos</span>
                      )}
                    </button>
                    <div className="flex h-full min-w-[18rem] flex-col overflow-hidden">
                    {isQuickTextsOpen && (
                      <>
                        <div className="flex items-center justify-between border-b border-[#d1d7db] bg-[#f0f2f5] px-4 py-3">
                          <span className="text-sm font-semibold text-[#111b21]">Respostas Rápidas</span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={openQuickTextCreate}
                              className="inline-flex items-center gap-1 rounded-lg bg-[#00a884] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#0b8f76]"
                              title="Nova resposta rápida"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                              Nova
                            </button>
                            <button
                              type="button"
                              onClick={() => setIsQuickTextsOpen(false)}
                              className="text-[#54656f] hover:text-[#111b21]"
                            >
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        </div>
                        <div className="border-b border-[#d1d7db] px-3 py-2">
                          <select
                            value={quickTextCategory}
                            onChange={(e) => setQuickTextCategory(e.target.value)}
                            className="mb-2 w-full rounded-lg border border-[#d1d7db] bg-white px-3 py-2 text-xs text-[#111b21] focus:border-[#00a884] focus:outline-none"
                          >
                            <option value="all">Todas as categorias</option>
                            {quickTexts.map((group) => (
                              <option key={group.category} value={group.category}>{group.category}</option>
                            ))}
                          </select>
                          <input
                            value={quickTextFilter}
                            onChange={(e) => setQuickTextFilter(e.target.value)}
                            placeholder="Buscar texto..."
                            className="w-full rounded-lg border border-[#d1d7db] bg-[#f0f2f5] px-3 py-2 text-xs text-[#111b21] placeholder:text-[#667781] focus:border-[#00a884] focus:outline-none"
                          />
                        </div>
                        <div className="flex-1 overflow-y-auto">
                          {quickTextModal && (
                            <div className="border-b border-[#d1d7db] bg-[#f7f9fa] px-3 py-3">
                              <p className="mb-2 text-xs font-semibold text-[#111b21]">
                                {quickTextModal.mode === 'create' ? '+ Nova Resposta' : 'Editar Resposta'}
                              </p>
                              <div className="space-y-2">
                                <div>
                                  <label className="mb-0.5 block text-[10px] font-medium text-[#667781]">Categoria</label>
                                  <input
                                    list="qt-categories"
                                    value={quickTextModal.category}
                                    onChange={(e) => setQuickTextModal((prev) => prev ? { ...prev, category: e.target.value } : null)}
                                    placeholder="Ex: Cumprimentos"
                                    className="w-full rounded-lg border border-[#d1d7db] bg-white px-2.5 py-1.5 text-xs text-[#111b21] placeholder:text-[#b0bec5] focus:border-[#00a884] focus:outline-none"
                                  />
                                  <datalist id="qt-categories">
                                    {quickTexts.map((g) => <option key={g.category} value={g.category} />)}
                                  </datalist>
                                </div>
                                <div>
                                  <label className="mb-0.5 block text-[10px] font-medium text-[#667781]">Rótulo</label>
                                  <input
                                    value={quickTextModal.label}
                                    onChange={(e) => setQuickTextModal((prev) => prev ? { ...prev, label: e.target.value } : null)}
                                    placeholder="Ex: Bom dia"
                                    className="w-full rounded-lg border border-[#d1d7db] bg-white px-2.5 py-1.5 text-xs text-[#111b21] placeholder:text-[#b0bec5] focus:border-[#00a884] focus:outline-none"
                                  />
                                </div>
                                <div>
                                  <label className="mb-0.5 block text-[10px] font-medium text-[#667781]">Mensagem</label>
                                  <textarea
                                    value={quickTextModal.text}
                                    onChange={(e) => setQuickTextModal((prev) => prev ? { ...prev, text: e.target.value } : null)}
                                    placeholder="Digite o texto da resposta..."
                                    rows={3}
                                    className="w-full resize-y rounded-lg border border-[#d1d7db] bg-white px-2.5 py-1.5 text-xs text-[#111b21] placeholder:text-[#b0bec5] focus:border-[#00a884] focus:outline-none"
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={submitQuickTextModal}
                                    className="flex-1 rounded-lg bg-[#00a884] py-1.5 text-xs font-medium text-white hover:bg-[#0b8f76]"
                                  >
                                    Salvar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setQuickTextModal(null)}
                                    className="flex-1 rounded-lg border border-[#d1d7db] py-1.5 text-xs font-medium text-[#54656f] hover:bg-[#f0f2f5]"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                          {quickTexts.map((group) => {
                            if (quickTextCategory !== 'all' && group.category !== quickTextCategory) return null;
                            const filtered = group.items.filter(
                              (item) =>
                                !quickTextFilter.trim() ||
                                item.label.toLowerCase().includes(quickTextFilter.toLowerCase()) ||
                                item.text.toLowerCase().includes(quickTextFilter.toLowerCase())
                            );
                            if (filtered.length === 0) return null;
                            return (
                              <div key={group.category}>
                                <div className="sticky top-0 bg-[#f0f2f5] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#667781]">
                                  {group.category}
                                </div>
                                {filtered.map((item) => (
                                  <div key={item.id} className="group relative border-b border-[#f0f2f5]">
                                    <button
                                      type="button"
                                      onClick={() => setDraft(item.text)}
                                      className="w-full px-3 py-2.5 pr-14 text-left hover:bg-[#f0f2f5]"
                                    >
                                      <p className="text-[11px] font-semibold text-[#00a884]">{item.label}</p>
                                      <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-[#54656f]">{item.text}</p>
                                    </button>
                                    <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); openQuickTextEdit(group.category, item); }}
                                        className="rounded p-1 text-[#54656f] hover:bg-[#e9edef] hover:text-[#111b21]"
                                        title="Editar"
                                      >
                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); deleteQuickText(group.category, item.id); }}
                                        className="rounded p-1 text-[#54656f] hover:bg-red-50 hover:text-red-500"
                                        title="Excluir"
                                      >
                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                    </div>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-h-full max-w-5xl" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/75"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={previewImage.url}
              alt={previewImage.alt}
              className="max-h-[88vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
            />
          </div>
        </div>
      )}
    </>
  );
}

